import { useState } from "react";
import { useWizard } from "../CalendarForm";
import {
  RangeSlider,
  CardSelector,
  SectionHeader,
  TextInput,
  ParameterTrioInput,
} from "../components/inputs";

// Icons
const BulkIcon = (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
      d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m0 8v2a2 2 0 01-2 2h-2M9 12h6m-3-3v6" />
  </svg>
);

// Configuration options
const RHYTHM_OPTIONS = [
  { id: "daily", label: "Daily", icon: "📅" },
  { id: "weekly", label: "Weekly", icon: "📆" },
  { id: "monthly", label: "Monthly", icon: "🗓️" },
];

// Time configuration based on rhythm
const TIME_CONFIG = {
  daily: { min: 1, max: 90, unit: "days" },
  weekly: { min: 1, max: 150, unit: "weeks" },
  monthly: { min: 1, max: 60, unit: "months" },
};

// Default parameters
const DEFAULT_SKIN_FOLDS = [
  { name: "", current: "", target: "" },
  { name: "", current: "", target: "" },
];

const DEFAULT_PERIMETERS = [
  { name: "", current: "", target: "" },
  { name: "", current: "", target: "" },
];

/**
 * BulkConfiguration Component
 * 
 * Step 2 configuration for muscle building.
 */
export default function BulkConfiguration() {
  const { formData, updateFormData } = useWizard();

  const [config, setConfig] = useState({
    // Body measurements
    morningWeight: formData.bulkConfig?.morningWeight || "",
    nightWeight: formData.bulkConfig?.nightWeight || "",
    currentFatPercentage: formData.bulkConfig?.currentFatPercentage || "",
    targetFatPercentage: formData.bulkConfig?.targetFatPercentage || "",
    leanMassGain: formData.bulkConfig?.leanMassGain || "",
    
    // Timing
    rhythm: formData.bulkConfig?.rhythm || "weekly",
    time: formData.bulkConfig?.time || 12,
    
    // Parameters to track
    skinFolds: formData.bulkConfig?.skinFolds || DEFAULT_SKIN_FOLDS,
    perimeters: formData.bulkConfig?.perimeters || DEFAULT_PERIMETERS,
  });

  const handleChange = (field, value) => {
    let newConfig = { ...config, [field]: value };
    
    // Reset time when rhythm changes
    if (field === "rhythm") {
      const timeConfig = TIME_CONFIG[value];
      newConfig.time = Math.min(Math.max(config.time, timeConfig.min), timeConfig.max);
    }
    
    setConfig(newConfig);
    updateFormData({ bulkConfig: newConfig });
  };

  const handleSkinFoldChange = (index, value) => {
    const newSkinFolds = [...config.skinFolds];
    newSkinFolds[index] = value;
    handleChange("skinFolds", newSkinFolds);
  };

  const handlePerimeterChange = (index, value) => {
    const newPerimeters = [...config.perimeters];
    newPerimeters[index] = value;
    handleChange("perimeters", newPerimeters);
  };

  const timeConfig = TIME_CONFIG[config.rhythm];

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Bulk Configuration"
        description="Customize your muscle-building program"
        icon={BulkIcon}
        color="blue"
      />

      {/* Body Measurements Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
        <h4 className="text-sm font-semibold text-gray-800">Body Measurements</h4>
        
        {/* Weight inputs */}
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="Morning Weight"
            value={config.morningWeight}
            onChange={(value) => handleChange("morningWeight", value)}
            type="number"
            placeholder="0.0"
            description="kg"
            color="blue"
          />
          <TextInput
            label="Night Weight"
            value={config.nightWeight}
            onChange={(value) => handleChange("nightWeight", value)}
            type="number"
            placeholder="0.0"
            description="kg"
            color="blue"
          />
        </div>

        {/* Fat percentage inputs */}
        <div className="grid grid-cols-2 gap-4">
          <TextInput
            label="Current Fat %"
            value={config.currentFatPercentage}
            onChange={(value) => handleChange("currentFatPercentage", value)}
            type="number"
            placeholder="0.0"
            description="%"
            color="blue"
          />
          <TextInput
            label="Target Fat %"
            value={config.targetFatPercentage}
            onChange={(value) => handleChange("targetFatPercentage", value)}
            type="number"
            placeholder="0.0"
            description="%"
            color="blue"
          />
        </div>

        {/* Lean mass gain */}
        <TextInput
          label="Lean Mass Gain Target"
          value={config.leanMassGain}
          onChange={(value) => handleChange("leanMassGain", value)}
          type="number"
          placeholder="0.0"
          description="kg"
          color="blue"
        />
      </div>

      {/* Timing Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
        <h4 className="text-sm font-semibold text-gray-800">Timing</h4>
        
        <CardSelector
          label="Rhythm"
          value={config.rhythm}
          onChange={(value) => handleChange("rhythm", value)}
          options={RHYTHM_OPTIONS}
          color="blue"
          columns={3}
        />

        <RangeSlider
          label="Duration"
          value={config.time}
          onChange={(value) => handleChange("time", value)}
          min={timeConfig.min}
          max={timeConfig.max}
          step={1}
          unit={` ${timeConfig.unit}`}
          color="blue"
        />
      </div>

      {/* Parameters to Track Section */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
        <h4 className="text-sm font-semibold text-gray-800">Parameters to Track</h4>
        
        <ParameterTrioInput.Group
          title="Skin Folds"
          description="Track skin fold measurements"
          parameters={config.skinFolds}
          onChange={handleSkinFoldChange}
          color="blue"
          unit="mm"
        />

        <div className="border-t border-gray-200 pt-4">
          <ParameterTrioInput.Group
            title="Body Perimeters"
            description="Track body circumference measurements"
            parameters={config.perimeters}
            onChange={handlePerimeterChange}
            color="blue"
            unit="cm"
          />
        </div>
      </div>

    </div>
  );
}